import { Loading } from '@components/atomic/atoms';
import { ConfirmModal, RejectModal } from '@components/atomic/organisms';
import SuccessModal from '@components/atomic/organisms/SucessModal/SucessModal';
import { ArrowRight, Lens, PersonCircle, Trash } from '@icons';
// import { getResumeInvoicesByProjectId, InvoiceType } from '@lib/api_ts/invoicesApi';
import {
	assignMassiveOwnership,
	assignMassiveOwnershipForSampling,
	getOwnershipStatus,
	getOwnershipStatusForSampling,
} from '@lib/api_ts/ownershipApi';
import { getProject } from '@lib/api_ts/projectApi';
import { getUsersByProjectId, UserType } from '@lib/api_ts/usersApi';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { Button, FormControl, Modal, Placeholder, Spinner } from 'react-bootstrap';
import { useLocation, useParams } from 'react-router-dom';

interface OwnershipModalProps {
	disabled: boolean;
	externalSuppliesCount: number;
	samplingId: number;
}

const OwnershipModal: React.FC<OwnershipModalProps> = ({ disabled, externalSuppliesCount, samplingId }) => {
	const queryClient = useQueryClient();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
	const [selectedUsers, setSelectedUsers] = useState<(UserType & { invoiceCount: number })[]>([]);
	const [searchTerm, setSearchTerm] = useState('');
	const [ownerDocumentChains, setOwnerDocumentChains] = useState<{
		[ownerId: number]: { documentChains: { id: number }[] };
	}>({});

	const [distributionStatus, setDistributionStatus] = useState<'needsDistribution' | 'distributed' | 'idle'>('idle');

	const { projectId } = useParams<{ projectId: string }>();
	const { search } = useLocation();
	const isCompleteList = search.includes('complete-list=true');

	const { data: project } = useQuery({
		queryKey: ['project', projectId],
		queryFn: () => getProject(projectId),
	});
	const { data: users, isPending: isPendingUsers } = useQuery({
		queryKey: ['usersPerProject', projectId],
		queryFn: () => getUsersByProjectId(projectId),
		enabled: !!projectId,
	});

	const { data: ownershipStatus, isPending: isPendingOwnershipStatus } = useQuery({
		queryKey: ['ownershipStatus', projectId, isModalOpen, samplingId, isCompleteList],
		queryFn: () => (samplingId && !isCompleteList ? getOwnershipStatusForSampling(projectId, samplingId) : getOwnershipStatus(projectId)),
		enabled: !!projectId && isModalOpen,
	});

	useEffect(() => {
		if (!ownershipStatus || ownershipStatus.length === 0) return;
		const initialOwnerDocumentChains = ownershipStatus.reduce(
			(acc, owner) => {
				acc[owner.userId] = { documentChains: Array.from({ length: owner.invoiceCount }, (_, index) => ({ id: index })) };
				return acc;
			},
			{} as { [ownerId: number]: { documentChains: { id: number }[] } },
		);

		setOwnerDocumentChains(initialOwnerDocumentChains);
		setSelectedUsers(
			ownershipStatus.map((owner) => ({
				id: owner.userId,
				email: owner.email,
				firstName: owner.firstName,
				lastName: owner.lastName,
				invoiceCount: owner.invoiceCount,
			})),
		);
		setDistributionStatus('distributed');
	}, [ownershipStatus]);

	const assignMassiveOwnershipMutation = useMutation({
		mutationFn: (owners: number[]) =>
			samplingId && !isCompleteList
				? assignMassiveOwnershipForSampling(projectId, samplingId, owners)
				: assignMassiveOwnership(projectId, owners),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['resumeInvoices', parseInt(projectId), 'externalSupplies'] });
			queryClient.invalidateQueries({ queryKey: ['ownershipStatus', projectId, isModalOpen] });
			if (samplingId) {
				queryClient.invalidateQueries({ queryKey: ['samplingStatistics', project.id, samplingId] });
			}
			setIsModalOpen(false);
			setSelectedUsers([]);
			console.log('Ownership assigned successfully');
		},
		onError: (error) => {
			console.error('Error assigning ownership:', error);
		},
	});

	// console.log("users", users)

	// const MOCK_CATENE_DOCUMENTALI = Array.from({ length: 666 },
	//     (_, index) => ({ id: index + 1 }));

	const distributeEvenlyDocumentChains = () => {
		// Quota base per ogni utente
		const chainsPerUser = Math.floor(externalSuppliesCount / selectedUsers.length);
		// Quante catene restano da distribuire
		let remainder = externalSuppliesCount % selectedUsers.length;

		// Costruisco un array di lunghezze per utente
		const chainsCountPerUser = selectedUsers.map(() => chainsPerUser);

		// Assegno le catene rimanenti partendo dall’ultimo utente verso il primo
		for (let index = selectedUsers.length - 1; index >= 0 && remainder > 0; index--) {
			chainsCountPerUser[index]++;
			remainder--;
		}

		// Creo la mappatura effettiva
		const newOwnerDocumentChains: {
			[ownerId: number]: { documentChains: { id: number }[] };
		} = {};

		let currentId = 0;
		selectedUsers.forEach((user, index) => {
			const count = chainsCountPerUser[index];
			const documentChains = Array.from({ length: count }, () => ({
				id: currentId++,
			}));
			newOwnerDocumentChains[user.id] = { documentChains };
		});

		setOwnerDocumentChains(newOwnerDocumentChains);
		setSelectedUsers((prevSelectedUsers) =>
			prevSelectedUsers.map((prevSelectedUser) => ({
				...prevSelectedUser,
				invoiceCount: newOwnerDocumentChains[prevSelectedUser.id]?.documentChains.length || 0,
			})),
		);
		setDistributionStatus('distributed');
	};

	// const distributeEvenlyDocumentChains = () => {
	// 	const totalChains = externalSuppliesCount;
	// 	const chainsPerUser = Math.floor(totalChains / selectedUsers.length);
	// 	const newOwnerDocumentChains: { [ownerId: number]: { documentChains: { id: number }[] } } = {};
	// 	selectedUsers.forEach((user, index) => {
	// 		const start = index * chainsPerUser;
	// 		const end = start + chainsPerUser;
	// 		newOwnerDocumentChains[user.id] = {
	// 			documentChains: Array.from({ length: end - start }, (_, index) => ({
	// 				id: start + index,
	// 			})),
	// 		};
	// 	});
	// 	setOwnerDocumentChains(newOwnerDocumentChains);
	// };

	const handleUserSelect = (user: UserType & { invoiceCount: number }) => {
		setSelectedUsers((prevSelectedUsers) => {
			if (prevSelectedUsers.some((prevSelectedUser) => prevSelectedUser.id === user.id)) {
				return prevSelectedUsers.filter((prevSelectedUser) => prevSelectedUser.id !== user.id);
			}
			return [...prevSelectedUsers, user];
		});
		setDistributionStatus('needsDistribution');
		setOwnerDocumentChains((prevChains) => {
			const updatedChains = { ...prevChains };
			delete updatedChains[user.id];
			return updatedChains;
		});
	};

	const noUsersSelected = selectedUsers.length === 0;

	return (
		<>
			<Button
				className="btn custom-primary-button d-flex justify-content-center align-items-center gap-2"
				style={{
					width: '285px',
					height: '48px',
					fontWeight: 500,
				}}
				onClick={() => {
					setIsModalOpen(true);
				}}
				disabled={disabled}
			>
				Gestisci Ownership <PersonCircle style={{ fill: disabled ? '#ABB7C3' : '#fff' }} />
			</Button>
			<Modal
				show={isModalOpen}
				onHide={() => {
					setIsModalOpen(false);
				}}
				centered
				fullscreen
				className="custom-modal"
			>
				<Modal.Header style={{ padding: '48px', paddingBottom: '8px' }} className="d-flex align-items-center">
					<Modal.Title style={{ fontSize: '40px' }}>Gestione Ownership</Modal.Title>
					<Button
						onClick={() => {
							setIsModalOpen(false);
						}}
						variant="link"
						className="d-flex align-items-center ms-auto"
						style={{
							gap: '0.5rem',
							textDecoration: 'none',
						}}
					>
						<ArrowRight style={{ rotate: '180deg' }} /> Indietro
					</Button>
				</Modal.Header>
				<Modal.Body
					style={{ padding: '48px', paddingTop: '8px', display: 'grid', gridTemplateRows: 'auto 1fr', height: '100%', overflowY: 'hidden' }}
				>
					<div className="mb-4">
						<p className="mb-4">Configura i responsabili titolari a svolgere l'attività di controllo.</p>
						<h3 style={{ fontSize: '24px', fontWeight: 700 }}>
							<span style={{ textTransform: 'uppercase' }}>Progetto:</span> {project?.name}
						</h3>
					</div>
					<div className="d-grid" style={{ gridTemplateColumns: '300px 1fr', gap: '48px', height: '100%', overflowY: 'hidden' }}>
						<div style={{ height: '100%', display: 'grid', gridTemplateRows: 'auto 1fr', overflowY: 'hidden' }}>
							<p className="fw-bold">Utenti da aggiungere al progetto:</p>
							<div
								style={{
									padding: '16px',
									backgroundColor: '#fff',
									borderRadius: '8px',
									boxShadow: '0px 2px 11px 0px #00000040',
									height: '100%',
									display: 'grid',
									gridTemplateRows: 'auto 1fr',
									overflowY: 'hidden',
								}}
							>
								<div style={{ position: 'relative' }} className="mb-3">
									<FormControl
										type="text"
										placeholder="Cerca una persona..."
										name="search"
										value={searchTerm}
										onChange={(event) => {
											setSearchTerm(event.target.value);
										}}
									/>
									<Lens style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }} />
								</div>
								<ul style={{ listStyle: 'none', padding: 0, margin: 0, paddingLeft: '8px', overflowY: 'auto' }}>
									{!!users && !isPendingUsers ? (
										users
											.filter((user) => `${user.firstName} ${user.lastName} ${user.id}`.toLowerCase().includes(searchTerm.toLowerCase()))
											.map((user) => (
												<li key={user.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
													<input
														id={`${user.id}-ownership-checkbox`}
														type="checkbox"
														style={{ borderColor: '#B3B3B3' }}
														checked={selectedUsers.some((selectedUser) => selectedUser.id === user.id)}
														onChange={() => {
															handleUserSelect({
																id: user.id,
																email: user.email,
																firstName: user.firstName,
																lastName: user.lastName,
																invoiceCount: ownerDocumentChains[user.id]?.documentChains.length || 0,
															});
														}}
													/>
													<label
														htmlFor={`${user.id}-ownership-checkbox`}
														style={{
															whiteSpace: 'nowrap',
															overflow: 'hidden',
															textOverflow: 'ellipsis',
														}}
													>
														{user.firstName} {user.lastName} - ID: {user.id}
													</label>
												</li>
											))
									) : (
										<Loading />
									)}
								</ul>
							</div>
						</div>
						<div style={{ height: '100%', display: 'grid', gridTemplateRows: 'auto 1fr auto', overflowY: 'hidden' }}>
							<div
								style={{
									backgroundColor: '#DEE4F1',
									padding: '1rem',
									borderRadius: '0.5rem',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									marginBottom: '1rem',
								}}
							>
								<div
									className="fw-bold"
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: '0.5rem',
									}}
								>
									<span>Catene documentali da assegnare:</span> {/* {isPendingChainDocuments || !chainDocuments ? <Loading /> : */}
									{/* {!externalSupplies ? <Loading /> : */}
									{isPendingOwnershipStatus || externalSuppliesCount === 0 ? (
										<Placeholder
											as="span"
											animation="glow"
											style={{
												fontSize: '40px',
												// height: '24px',
												width: '100px',
											}}
										>
											<Placeholder xs={10} />
										</Placeholder>
									) : (
										<span style={{ fontSize: '40px' }}>
											{/* {chainDocuments.length - Object.values(ownerDocumentChains).flatMap(chain => chain.documentChains).length}/{chainDocuments.length} */}
											{Math.max(
												externalSuppliesCount - Object.values(ownerDocumentChains).flatMap((chain) => chain.documentChains).length,
												0,
											)}
											/{externalSuppliesCount}
										</span>
									)}
									{/* } */}
								</div>
								<Button
									disabled={distributionStatus !== 'needsDistribution'}
									className="btn custom-primary-button"
									style={{ fontWeight: 500 }}
									onClick={distributeEvenlyDocumentChains}
								>
									Distribuisci
								</Button>
							</div>
							<div
								style={{ backgroundColor: '#fff', borderRadius: '0.5rem', padding: '1.5rem', marginBottom: '2rem', overflowY: 'hidden' }}
							>
								{isPendingOwnershipStatus ? (
									<div
										className="d-grid align-items-center justify-content-center"
										style={{ height: '100%', color: '#C7CFD7', fontSize: '28px' }}
									>
										<Loading />
									</div>
								) : null}
								{noUsersSelected ? (
									<div
										className="d-grid align-items-center justify-content-center"
										style={{ height: '100%', color: '#C7CFD7', fontSize: '28px' }}
									>
										Aggiungi persone dalla lista da associare al progetto
									</div>
								) : null}

								{!noUsersSelected ? (
									<ul style={{ listStyle: 'none', padding: 0, margin: 0, overflowY: 'auto', height: '100%' }}>
										{selectedUsers.map((user) => (
											<li
												key={user.id}
												style={{
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'space-between',
													gap: '4rem',
													borderBottom: '1px solid #E0E0E0',
													paddingBlock: '1rem',
												}}
											>
												<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
													<Trash
														style={{ cursor: 'pointer', fill: '#CC334C' }}
														onClick={() => {
															handleUserSelect(user);
														}}
													/>
													<span>
														{user.firstName} {user.lastName} - ID {user.id}
													</span>
												</div>
												<div className="ms-auto">
													<span className="fw-bold">email:</span> <span>{user.email}</span>
												</div>
												<div style={{ display: 'flex', alignItems: 'center', gap: '3rem' }}>
													<div>Catene documentali assegnate</div>
													<div className="fw-bold" style={{ padding: '0.5rem', paddingRight: '3rem' }}>
														{user.invoiceCount}
													</div>
												</div>
											</li>
										))}
									</ul>
								) : null}
							</div>
							<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
								<div>
									<Button
										onClick={() => {
											setIsConfirmModalOpen(true);
										}}
										disabled={noUsersSelected}
										className="btn custom-ghost-button d-flex align-items-center"
										style={{ gap: '0.5rem', color: noUsersSelected ? '#4A5968' : '#CC334C' }}
									>
										<Trash style={{ fill: noUsersSelected ? '#4A5968' : '#CC334C' }} />
										Resetta tutto
									</Button>
									<ConfirmModal
										title="Sei sicuro di voler resettare tutti i dati inseriti?"
										message="L'operazione non è reversibile."
										show={isConfirmModalOpen}
										onHide={() => {
											setIsConfirmModalOpen(false);
										}}
										callback={() => {
											setOwnerDocumentChains({});
											setSelectedUsers([]);
										}}
										confirmButtonLabel={
											<div className="d-flex align-items-center justify-content-center gap-2">
												<Trash />
												Sì, resetta
											</div>
										}
										confirmButtonClassName="custom-error-button"
									/>
								</div>
								<div style={{ display: 'flex', gap: '0.5rem' }}>
									<Button
										onClick={() => {
											setIsModalOpen(false);
											setSelectedUsers([]);
										}}
										className="btn custom-outline-button"
										style={{ width: '285px' }}
									>
										Annulla
									</Button>
									<Button
										onClick={() => {
											assignMassiveOwnershipMutation.mutate(selectedUsers.map((user) => user.id));
										}}
										disabled={noUsersSelected || distributionStatus !== 'distributed' || assignMassiveOwnershipMutation.isPending}
										className="btn custom-primary-button"
										style={{ width: '285px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem' }}
									>
										Conferma {assignMassiveOwnershipMutation.isPending ? <Spinner animation="border" size="sm" /> : null}
									</Button>
								</div>
							</div>
						</div>
					</div>
				</Modal.Body>
			</Modal>
			<SuccessModal
				message="Ownership modificata con successo!"
				show={assignMassiveOwnershipMutation.isSuccess}
				onHide={assignMassiveOwnershipMutation.reset}
			/>
			<RejectModal
				message="Si è verificato un errore durante la modifica dell'ownership."
				show={assignMassiveOwnershipMutation.isError}
				onHide={assignMassiveOwnershipMutation.reset}
				tryAgain={() => {
					const owners = selectedUsers.map((user) => user.id);
					assignMassiveOwnershipMutation.mutate(owners);
				}}
			/>
		</>
	);
};

export default OwnershipModal;
